/**
 * app.js
 * Main application entry point
 * Initializes all modules and sets up the application
 */

import { initElements, elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { CONFIG, AGENTS } from './config.js?v=302';
import { cleanupDeprecatedKeys, pruneOldChats, getDraft } from './storage.js?v=302';
import { applyTheme, renderThemesTab, initFontCustomization } from './themes.js?v=302';
import { renderEmptyState, applySidebarState, renderChatList } from './ui.js?v=302';
import { loadChat, createNewChat, sendMessage } from './chat.js?v=302';
import { handleGlobalKeyboardShortcuts } from './keyboard.js?v=302';
import { autoResizeInput } from './utils.js?v=302';
import { setupEventListeners } from './eventListeners.js?v=302';
import './linkPreview.js?v=302';

// Expose functions to window for inline onclick handlers
window.createNewChat = createNewChat;
/**
 * Token validation
 * Checks URL or localStorage for valid token before allowing access
 */
function validateToken() {
  const urlParams = new URLSearchParams(window.location.search);
  const urlToken = urlParams.get("token");
  const storedToken = localStorage.getItem("eliza-auth-token");
  
  // If token in URL, validate and store it
  if (urlToken) {
    if (urlToken === CONFIG.authToken) {
      localStorage.setItem("eliza-auth-token", urlToken);
      // Clean URL (remove token from address bar for security)
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    }
    return false; // Invalid token in URL
  }
  
  // Check stored token
  if (storedToken && storedToken === CONFIG.authToken) {
    return true;
  }
  
  return false;
}

/**
 * Show access denied page
 */
function showAccessDenied() {
  document.body.innerHTML = `
    <div style="
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100vh;
      background: #1a1a2e;
      color: #fff;
      font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif;
      text-align: center;
      padding: 20px;
    ">
      <div style="font-size: 64px; margin-bottom: 20px;">🔒</div>
      <h1 style="margin: 0 0 16px 0; font-size: 28px; font-weight: 600;">Access Denied</h1>
      <p style="margin: 0; color: #888; font-size: 16px; max-width: 400px;">
        This webchat requires a valid authentication token.<br><br>
        Please use the correct URL with your access token.
      </p>
    </div>
  `;
}



/**
 * Update connection status
 * Just shows the model name - no blocking API call on startup
 */
async function updateConnectionStatus() {
  const statusEl = document.getElementById("connection-status");
  if (!statusEl) return;

  const activeAgent = AGENTS.find(a => a.id === state.selectedAgent) || AGENTS[0];
  statusEl.innerHTML = `🟢 Ready • ${activeAgent.name}`;
  statusEl.style.color = "var(--success)";
}

/**
 * Initialize agent selector dropdown
 */
function initAgentSelector() {
  const selector = elements.agentSelector;
  const btn = elements.agentSelectorBtn;
  const dropdown = elements.agentDropdown;
  const emojiEl = elements.agentEmoji;
  const nameEl = elements.agentName;

  if (!selector || !btn || !dropdown) return;

  // Populate dropdown
  dropdown.innerHTML = AGENTS.map(agent => `
    <button class="agent-option ${agent.id === state.selectedAgent ? 'active' : ''}"
            data-agent-id="${agent.id}" role="option"
            aria-selected="${agent.id === state.selectedAgent}">
      <span class="agent-option-emoji">${agent.emoji}</span>
      <span class="agent-option-info">
        <span class="agent-option-name">${agent.name}</span>
        <span class="agent-option-id">${agent.id}</span>
      </span>
      <svg class="agent-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
    </button>
  `).join('');

  // Set initial display
  const current = AGENTS.find(a => a.id === state.selectedAgent) || AGENTS[0];
  emojiEl.textContent = current.emoji;
  nameEl.textContent = current.name;

  // Toggle dropdown
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    selector.classList.toggle('open');
  });

  // Close on outside click
  document.addEventListener('click', (e) => {
    if (!selector.contains(e.target)) {
      selector.classList.remove('open');
    }
  });

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      selector.classList.remove('open');
    }
  });

  // Agent selection
  dropdown.addEventListener('click', (e) => {
    const option = e.target.closest('.agent-option');
    if (!option) return;

    const agentId = option.dataset.agentId;
    const agent = AGENTS.find(a => a.id === agentId);
    if (!agent) return;

    // Update state
    state.selectedAgent = agentId;
    localStorage.setItem('chatbot_agent', agentId);

    // Update button display
    emojiEl.textContent = agent.emoji;
    nameEl.textContent = agent.name;

    // Update active states
    dropdown.querySelectorAll('.agent-option').forEach(opt => {
      const isActive = opt.dataset.agentId === agentId;
      opt.classList.toggle('active', isActive);
      opt.setAttribute('aria-selected', isActive);
    });

    // Update connection status
    updateConnectionStatus();

    // Close dropdown
    selector.classList.remove('open');

    // Toast feedback
    import('./ui.js?v=302').then(({ showToast }) => {
      showToast(`Switched to ${agent.emoji} ${agent.name}`, 'info');
    });
  });
}

/**
 * Restore draft message from localStorage
 * Loads saved draft for current chat and resizes input
 */
function restoreDraft() {
  const draft = getDraft(state.currentChatId);
  if (draft) {
    elements.input.value = draft;
    autoResizeInput();
  }
}

/**
 * Main initialization function
 * Sets up the application on page load
 */
function init() {
  // Initialize DOM element references
  initElements();

  // Configure marked: open all links in new tabs
  if (window.marked) {
    const renderer = new window.marked.Renderer();
    const origLink = renderer.link.bind(renderer);
    renderer.link = function (href, title, text) {
      // marked v11+ passes an object; v4 passes positional args
      const html = origLink(href, title, text);
      // Add target="_blank" and rel="noopener noreferrer" to all links
      return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ');
    };
    window.marked.setOptions({ renderer });
  }

  // Clean up legacy keys from localStorage
  cleanupDeprecatedKeys();

  // Prune conversations older than 7 days
  pruneOldChats(7);

  // Apply theme and UI state
  applyTheme();
  renderThemesTab();
  applySidebarState();
  renderEmptyState();
  initFontCustomization();

  // Setup all event listeners
  setupEventListeners();

  // Initialize agent selector
  initAgentSelector();

  // Update connection status
  updateConnectionStatus();

  // Load current chat if exists
  if (state.currentChatId && state.chats.some(c => c.id === state.currentChatId)) {
    loadChat(state.currentChatId);
  } else {
    renderChatList();
  }

  // Restore draft message and setup keyboard shortcuts
  restoreDraft();
  document.addEventListener('keydown', handleGlobalKeyboardShortcuts);
}

/**
 * Initialize application with safeguards
 * Ensures init only runs once and after DOM is ready
 */
let initialized = false;
function initWrapper() {
  if (initialized) return;
  initialized = true;
  
  // Validate token before allowing access
  if (!validateToken()) {
    showAccessDenied();
    return;
  }
  
  init();
}

// Initialize when DOM is ready
document.addEventListener("DOMContentLoaded", initWrapper);
if (document.readyState !== "loading") {
  initWrapper();
}
